import React, { FC, useCallback } from "react";
import { polkadotIcon, beachballIcon } from "@polkadot/ui-shared";
import type { Circle } from "@polkadot/ui-shared/icons/types";

interface AddressAvatarProps {
  address?: string;
  size?: number;
  theme?: "polkadot" | "beachball";
  className?: string;
}

/**
 * The renderCircle function and Identicon component are taken from
 * https://github.com/polkadot-js/ui/blob/master/packages/react-identicon/src/icons/Polkadot.tsx
 *
 * Due to compatability issues with NextJS, older versions of React, and potentially yarn
 * the packages could not be imported properly.
 */
const renderCircle = (
  { cx, cy, fill, r }: Circle,
  key: number
): React.ReactNode => {
  return <circle cx={cx} cy={cy} fill={fill} key={key} r={r} />;
};

const Identicon: React.FC<{
  address: string;
  size: number;
  theme: "polkadot" | "beachball";
}> = ({ address, size, theme }) => {
  const updateElem = useCallback(
    (node: HTMLDivElement): void => {
      node &&
        node.appendChild(
          beachballIcon(address, { isAlternative: false, size })
        );
    },
    [address, size]
  );

  if (theme === "polkadot") {
    return (
      <svg
        className="m-2"
        height={size}
        id={address}
        name={address}
        style={{}}
        viewBox="0 0 64 64"
        width={size}
      >
        {polkadotIcon(address, { isAlternative: false }).map(renderCircle)}
      </svg>
    );
  } else {
    return <div className="flex justify-center items-center p-2" ref={updateElem}></div>;
  }
};

export const AddressAvatar: FC<AddressAvatarProps> = ({
  address,
  className = "",
  size = 32,
  theme,
}) => {
  return (
    <div
      onClick={() => navigator.clipboard.writeText(address!)}
      className={`flex-center w-max cursor-pointer rounded-full ${className}`}
    >
      <Identicon size={size} address={address!} theme={theme ?? "polkadot"} />
    </div>
  );
};
